import moment from 'moment-timezone'
import type {OperationLocationT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {parseOperationLocationHeader} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {split} from '@freckle/educator-entities/ts/common/helpers/common-helper'
import type {SyncResultT} from '@freckle/educator-entities/ts/common/models/roster-sync'
import {
  extractData,
  fancyClient,
  ensureOk,
  extractHeaders,
  formDataSerializer,
} from '@freckle/educator-entities/ts/openapi-clients'
import type {UnmanagedCourseT} from '@freckle/school/ts/common/models/unmanaged-course'
import type {AllRosterFiles} from '@freckle/school/ts/rostering/components/import-roster/import-roster-container'

/**
 * CSV File Roster Preview
 */

const dryRunUploadRoster = (schoolAdminId: number, files: AllRosterFiles): Promise<SyncResultT[]> =>
  fancyClient
    .PATCH('/2/school-admins/{SchoolAdminP-SchoolAdminId}/rosters', {
      bodySerializer: formDataSerializer,
      body: {
        'classes.csv': files.courses,
        'students.csv': files.students,
        'teachers.csv': files.teachers,
        'enrollments.csv': files.enrollments,
      },
      params: {
        path: {
          'SchoolAdminP-SchoolAdminId': schoolAdminId,
        },
        query: {
          'dry-run': true,
        },
      },
    })
    .then(extractData)
    .then(data => data as SyncResultT[])

const uploadRoster = (files: AllRosterFiles): Promise<OperationLocationT> =>
  fancyClient
    .PATCH('/3/rosters', {
      bodySerializer: formDataSerializer,
      body: {
        'classes.csv': files.courses,
        'students.csv': files.students,
        'teachers.csv': files.teachers,
        'enrollments.csv': files.enrollments,
      },
    })
    .then(extractHeaders)
    .then(headers => {
      const operationLocationHeader = headers.get('Operation-Location')
      return parseOperationLocationHeader(operationLocationHeader)
    })

type ProgressHandlerT<A> = (finished: Array<A>, remaining: Array<A>, cancel: () => void) => void

/**
 * Deduplication API
 */

// The student merging route accepts a maximum of 20 MatchGroups. The course
// archival route is effectively unlimited, but we use the same batching to
// show progress.
const MAXIMUM_BATCH_SIZE = 20

// Merge all extant duplicate students in a set of schools
const mergeDuplicateStudentsForSchools = (schoolIds: Array<number>): Promise<OperationLocationT> =>
  fancyClient
    .POST('/3/students/duplicates/merge', {
      params: {
        query: {schoolIds},
      },
    })
    .then(extractHeaders)
    .then(headers => {
      const operationLocationHeader = headers.get('Operation-Location')
      return parseOperationLocationHeader(operationLocationHeader)
    })

const mergeDuplicateStudentsForCsvSync = (files: AllRosterFiles): Promise<OperationLocationT> =>
  fancyClient
    .POST('/3/students/duplicates/merge', {
      bodySerializer: formDataSerializer,
      body: {
        'classes.csv': files.courses,
        'students.csv': files.students,
        'teachers.csv': files.teachers,
        'enrollments.csv': files.enrollments,
      },
    })
    .then(extractHeaders)
    .then(headers => {
      const operationLocationHeader = headers.get('Operation-Location')
      return parseOperationLocationHeader(operationLocationHeader)
    })

/**
 * Course Archival API
 */

function findUnmanagedCourses(schoolIds: Array<number>): Promise<Array<UnmanagedCourseT>> {
  return fancyClient
    .GET('/2/courses/unmanaged', {params: {query: {schoolIds}}})
    .then(extractData)
    .then(data =>
      data.map(c => ({
        ...c,
        createdAt: moment(c.createdAt),
        courseSisId: c.courseSisId ?? null,
      })),
    )
}

function archiveUnmanagedCourses(
  courses: Array<UnmanagedCourseT>,
  handleProgress?: ProgressHandlerT<UnmanagedCourseT>,
): Promise<Array<UnmanagedCourseT>> {
  async function deleteCoursesBatch(batch: Array<UnmanagedCourseT>): Promise<void> {
    const courseIds = batch.map(course => course.id)
    await fancyClient
      .DELETE('/2/courses/unmanaged', {
        params: {
          query: {
            courseIds,
          },
        },
      })
      .then(ensureOk)
  }

  return processBatches(MAXIMUM_BATCH_SIZE, deleteCoursesBatch, courses, handleProgress)
}

// Internal function for batching requests and showing progress
function processBatches<A>(
  size: number,
  effect: (batch: Array<A>) => Promise<void>,
  items: Array<A>,
  handleProgress?: ProgressHandlerT<A>,
): Promise<Array<A>> {
  // Private cancellation token
  class Cancel {}

  function loop(finished: Array<A>, remaining: Array<A>): Promise<Array<A>> {
    // Return all groups once we're done
    if (remaining.length === 0) {
      return new Promise(resolve => resolve(finished))
    }

    // Let client see status and cancel early
    if (handleProgress) {
      try {
        handleProgress(finished, remaining, () => {
          throw new Cancel()
        })
      } catch (e) {
        if (e instanceof Cancel) {
          return new Promise(resolve => resolve(finished))
        } else {
          throw e
        }
      }
    }

    // Split remaining into first |size| groups and new remainder
    const {prefix, suffix} = split(size, remaining)

    return effect(prefix).then(() => loop([...finished, ...prefix], suffix))
  }
  return loop([], items)
}

const RosterApi = {
  previewRoster: dryRunUploadRoster,

  uploadRoster,

  mergeDuplicateStudentsForSchools,

  mergeDuplicateStudentsForCsvSync,

  findUnmanagedCourses,

  archiveUnmanagedCourses,
}

export default RosterApi
